/**
 * Election forecasting metrics using uncertainty-aware probability model
 *
 * Key Principles:
 * 1. Win probability reflects both expected margin AND confidence
 * 2. Low confidence shrinks probability toward 50% (honest uncertainty)
 * 3. Incumbency applied as margin shift (defensible, citable)
 *
 * References:
 * - Normal CDF approach: Statistical Modeling, Gelman et al.
 * - Incumbency: Journal of Public Affairs (2.6pp advantage)
 * - Brier score calibration: Wikipedia, forecast evaluation
 */

// === UTILITY FUNCTIONS ===

const clamp01 = (x) => Math.max(0, Math.min(1, x));
const clamp = (x, min, max) => Math.max(min, Math.min(max, x));

// Approximate normal CDF using error function (Abramowitz & Stegun)
function erf(x) {
  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * x);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}

function normalCdf(z) {
  return 0.5 * (1 + erf(z / Math.SQRT2));
}

// === CALIBRATION PARAMETERS ===

const PARAMS = {
  // Incumbency advantage (Journal of Public Affairs, 2022)
  INCUMBENCY_SHIFT: 2.6, // percentage points

  // Uncertainty range (will be calibrated with Brier score)
  SIGMA_HIGH_CONFIDENCE: 3.0,  // σ when confidence = 1.0
  SIGMA_LOW_CONFIDENCE: 9.0,   // σ when confidence = 0.0

  // Competitiveness weights (empirically tuned)
  COMP_WEIGHT_CLOSENESS: 0.55,
  COMP_WEIGHT_UNCERTAINTY: 0.20,
  COMP_WEIGHT_OPEN_SEAT: 0.15,
  COMP_WEIGHT_FUNDS: 0.10
};

// === VALIDATION ===

function validateDistrict(district) {
  const errors = [];

  if (district.margin_2024 === null || district.margin_2024 === undefined) {
    errors.push('Missing margin_2024');
  }

  if (district.model_confidence != null) {
    if (district.model_confidence < 0 || district.model_confidence > 1) {
      errors.push('model_confidence out of range [0,1]');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    // Safe fallbacks
    margin: district.margin_2024 ?? 0,
    confidence: clamp01(district.model_confidence ?? 0.7),
    isOpenSeat: Boolean(district.is_open_seat),
    incumbentParty: district.incumbent_party || null
  };
}

// === MAIN FUNCTIONS ===

/**
 * Calculate Democratic win probability
 *
 * Uses normal CDF: P(D wins) = Φ(μ / σ)
 * where μ = expected margin (with incumbency) and σ = uncertainty
 *
 * @param {Object} district - District data
 * @returns {number} Probability from 0 to 1
 */
export function calculateWinProbabilityD(district) {
  const { margin, confidence, isOpenSeat, incumbentParty, isValid, errors } =
    validateDistrict(district);

  if (!isValid) {
    console.warn(`Invalid district data for ${district.district_code}:`, errors);
  }

  // Convert confidence to standard deviation
  // High confidence → low σ (narrow uncertainty)
  // Low confidence → high σ (wide uncertainty)
  const sigma = PARAMS.SIGMA_LOW_CONFIDENCE +
    (PARAMS.SIGMA_HIGH_CONFIDENCE - PARAMS.SIGMA_LOW_CONFIDENCE) * confidence;

  // Incumbency as margin shift (NOT probability boost)
  // More defensible: "Incumbency worth ~2.6 points"
  let incumbencyShift = 0;
  if (!isOpenSeat && incumbentParty) {
    incumbencyShift = incumbentParty === 'Democratic'
      ? PARAMS.INCUMBENCY_SHIFT
      : -PARAMS.INCUMBENCY_SHIFT;
  }

  // Expected margin: observed + incumbency
  const expectedMargin = margin + incumbencyShift;

  // Win probability via normal CDF
  const probability = normalCdf(expectedMargin / sigma);

  return clamp01(probability);
}

/**
 * Calculate competitiveness score (0-100)
 *
 * Composite score identifying most competitive races:
 * - High closeness (near 50/50)
 * - High uncertainty (low confidence)
 * - Open seat bonus
 * - Fundraising parity bonus
 *
 * @param {Object} district - District data
 * @returns {number} Score from 0 to 100
 */
export function calculateCompetitivenessScore(district) {
  const pD = calculateWinProbabilityD(district);
  const { confidence, isOpenSeat } = validateDistrict(district);

  // 1. Closeness: highest at 50/50, lowest at 0/100
  const closeness = 1 - Math.abs(pD - 0.5) * 2;  // 0 to 1

  // 2. Uncertainty: REWARD low confidence (uncertain = competitive)
  const uncertainty = 1 - confidence;  // 0 to 1

  // 3. Open seat: bonus for opportunity
  const openSeatScore = isOpenSeat ? 1 : 0;

  // 4. Fundraising parity (when available)
  // TODO: Calculate from actual fundraising data
  const fundsParity = district.fundraising_parity ?? 0.5;  // 0 to 1

  // Weighted composite (tune weights based on user feedback)
  const score01 =
    PARAMS.COMP_WEIGHT_CLOSENESS * closeness +
    PARAMS.COMP_WEIGHT_UNCERTAINTY * uncertainty +
    PARAMS.COMP_WEIGHT_OPEN_SEAT * openSeatScore +
    PARAMS.COMP_WEIGHT_FUNDS * fundsParity;

  return Math.round(score01 * 100);
}

/**
 * Get confidence level label
 *
 * @param {number} confidence - Confidence value (0-1)
 * @returns {string} 'High', 'Medium', or 'Low'
 */
export function getConfidenceLabel(confidence) {
  if (confidence >= 0.8) return 'High';
  if (confidence >= 0.6) return 'Medium';
  return 'Low';
}

/**
 * Get party and win percentage for display
 *
 * @param {number} pD - Democratic win probability (0-1)
 * @returns {Object} { party: 'D'|'R', percentage: number }
 */
export function getWinDisplay(pD) {
  if (pD >= 0.5) {
    return {
      party: 'D',
      percentage: Math.round(pD * 100),
      margin: 'Democratic'
    };
  } else {
    return {
      party: 'R',
      percentage: Math.round((1 - pD) * 100),
      margin: 'Republican'
    };
  }
}

/**
 * Get OKLCH gradient color for probability
 *
 * Interpolates between anchor colors in OKLCH space
 * for perceptually uniform transitions
 *
 * @param {number} pD - Democratic win probability (0-1)
 * @param {number} confidence - Confidence level (0-1)
 * @returns {string} OKLCH color string
 */
export function getGradientColor(pD, confidence = 1.0) {
  const prob = clamp01(pD);

  // OKLCH anchor points (L, C, H)
  const DEEP_RED = [45, 0.20, 25];    // R 100%
  const RED = [55, 0.18, 25];         // R 75%
  const PURPLE = [60, 0.22, 300];     // Even
  const BLUE = [55, 0.18, 250];       // D 75%
  const DEEP_BLUE = [45, 0.20, 250];  // D 100%

  let l, c, h;

  if (prob < 0.25) {
    // Deep red → Red
    const t = prob * 4;
    l = DEEP_RED[0] + (RED[0] - DEEP_RED[0]) * t;
    c = DEEP_RED[1] + (RED[1] - DEEP_RED[1]) * t;
    h = DEEP_RED[2] + (RED[2] - DEEP_RED[2]) * t;
  } else if (prob < 0.5) {
    // Red → Purple
    const t = (prob - 0.25) * 4;
    l = RED[0] + (PURPLE[0] - RED[0]) * t;
    c = RED[1] + (PURPLE[1] - RED[1]) * t;
    h = RED[2] + (PURPLE[2] - RED[2]) * t;
  } else if (prob < 0.75) {
    // Purple → Blue
    const t = (prob - 0.5) * 4;
    l = PURPLE[0] + (BLUE[0] - PURPLE[0]) * t;
    c = PURPLE[1] + (BLUE[1] - BLUE[1]) * t;
    h = PURPLE[2] + (BLUE[2] - BLUE[2]) * t;
  } else {
    // Blue → Deep blue
    const t = (prob - 0.75) * 4;
    l = BLUE[0] + (DEEP_BLUE[0] - BLUE[0]) * t;
    c = BLUE[1] + (DEEP_BLUE[1] - BLUE[1]) * t;
    h = BLUE[2] + (DEEP_BLUE[2] - DEEP_BLUE[2]) * t;
  }

  // Desaturate for low confidence
  if (confidence < 1.0) {
    const desaturationFactor = 0.3 * (1 - confidence);
    c = c * (1 - desaturationFactor);
    l = l + (60 - l) * desaturationFactor;  // Move toward neutral gray
  }

  return `oklch(${l}% ${c} ${h})`;
}

/**
 * Batch process districts with derived metrics
 *
 * Use this in TanStack Query's select option for performance
 *
 * @param {Array} districts - Raw district array
 * @returns {Array} Districts with computed fields
 */
export function enrichDistricts(districts) {
  if (!Array.isArray(districts)) return [];

  return districts.map(d => {
    const pD = calculateWinProbabilityD(d);
    const compScore = calculateCompetitivenessScore(d);
    const { confidence } = validateDistrict(d);

    return {
      ...d,
      // Derived fields
      winProbD: pD,
      winProbR: 1 - pD,
      competitivenessScore: compScore,
      confidenceLabel: getConfidenceLabel(confidence),
      winDisplay: getWinDisplay(pD),
      gradientColor: getGradientColor(pD, confidence),
      // For sorting/filtering
      isCompetitive: compScore >= 70,
      isTossup: pD >= 0.4 && pD <= 0.6
    };
  });
}
